import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

function authHeaders() {
  const token = typeof window !== 'undefined' ? localStorage.getItem('adeeb-token') : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const STATUS_TONES = {
  pending:   { label: 'Pending',   tone: 'warn' },
  active:    { label: 'Active',    tone: 'good' },
  suspended: { label: 'Suspended', tone: 'muted' },
};

// Stripe subscription status → UI tone. We mirror what Stripe sends so a
// quick glance tells you billing health: green=paying, amber=needs attn,
// red=broken, grey=not yet billing or cancelled.
const SUB_TONES = {
  active:              { label: 'Active',         tone: 'good' },
  trialing:            { label: 'Trial',          tone: 'good' },
  past_due:            { label: 'Past due',       tone: 'warn' },
  unpaid:              { label: 'Unpaid',         tone: 'bad'  },
  canceled:            { label: 'Cancelled',      tone: 'muted' },
  incomplete:          { label: 'Incomplete',     tone: 'warn' },
  incomplete_expired:  { label: 'Failed',         tone: 'bad'  },
  paused:              { label: 'Paused',         tone: 'muted' },
};

function fmtPrice(amount, currency) {
  if (!amount) return '—';
  const sym = currency === 'GBP' ? '£' : currency === 'EUR' ? '€' : currency === 'USD' ? '$' : '';
  return `${sym}${(amount / 100).toFixed(2)}/mo`;
}

export default function AdminSchools() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [err, setErr] = useState('');
  const [toast, setToast] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null); // school object
  // Populated when the server returns an adminInitialPassword (on create
  // or first approval). Pops a "share once" modal so the admin sees the
  // credentials clearly instead of having to scrape the JSON response.
  const [credsToShare, setCredsToShare] = useState(null);
  const [form, setForm] = useState({
    name: '', country: '', address: '', contactName: '', contactEmail: '',
    monthlyPrice: '', currency: 'GBP', notes: '', status: 'active',
  });

  const ping = (m) => { setToast(m); setTimeout(() => setToast(''), 1800); };

  const load = async () => {
    setLoading(true); setErr('');
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (query.trim()) params.q = query.trim();
      const { data } = await axios.get('/api/admin/schools', {
        headers: authHeaders(),
        params,
      });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(e?.response?.data?.message || 'Failed to load.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);
  // Debounce search query
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [query]);

  // Dual scrollbars (top + bottom). The top div is a fake scroll-only
  // surface; we mirror its width to the table's scrollWidth and sync
  // the two scroll positions in both directions.
  const tableWrapRef = useRef(null);
  const topScrollRef = useRef(null);
  const topInnerRef  = useRef(null);
  useEffect(() => {
    const wrap = tableWrapRef.current;
    const top  = topScrollRef.current;
    const inner = topInnerRef.current;
    if (!wrap || !top || !inner) return;
    const sync = () => {
      const tableEl = wrap.querySelector('.admin-table');
      if (tableEl) inner.style.width = tableEl.scrollWidth + 'px';
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(wrap);
    let lock = false;
    const onTop  = () => { if (lock) return; lock = true; wrap.scrollLeft = top.scrollLeft; lock = false; };
    const onWrap = () => { if (lock) return; lock = true; top.scrollLeft  = wrap.scrollLeft; lock = false; };
    top.addEventListener('scroll',  onTop);
    wrap.addEventListener('scroll', onWrap);
    return () => {
      ro.disconnect();
      top.removeEventListener('scroll',  onTop);
      wrap.removeEventListener('scroll', onWrap);
    };
  }, [items]);

  const startCreate = () => {
    setEditing(null);
    setCreating(true);
    setForm({
      name: '', country: '', address: '', contactName: '', contactEmail: '',
      monthlyPrice: '', currency: 'GBP', notes: '', status: 'active',
    });
  };

  const startEdit = (s) => {
    setCreating(false);
    setEditing(s);
    setForm({
      name: s.name || '',
      country: s.country || '',
      address: s.address || '',
      contactName: s.contactName || '',
      contactEmail: s.contactEmail || '',
      monthlyPrice: s.monthlyPrice ? String((s.monthlyPrice / 100).toFixed(2)) : '',
      currency: s.currency || 'GBP',
      notes: s.notes || '',
      status: s.status || 'active',
    });
  };

  const cancel = () => { setCreating(false); setEditing(null); };

  const save = async () => {
    const payload = {
      name: form.name.trim(),
      country: form.country.trim(),
      address: form.address.trim(),
      contactName: form.contactName.trim(),
      contactEmail: form.contactEmail.trim(),
      monthlyPrice: Math.round((parseFloat(form.monthlyPrice) || 0) * 100),
      currency: form.currency || 'GBP',
      notes: form.notes,
      status: form.status,
    };
    if (!payload.name) { setErr('School name is required.'); return; }
    try {
      if (creating) {
        const { data } = await axios.post('/api/admin/schools', payload, { headers: authHeaders() });
        ping('School created');
        cancel();
        if (data?.adminInitialPassword && data?.adminUser?.email) {
          setCredsToShare({
            label: `${data.name} school admin`,
            email: data.adminUser.email,
            password: data.adminInitialPassword,
          });
        }
        if (statusFilter !== 'all' && data?.status !== statusFilter) {
          setStatusFilter('all'); // triggers load via the filter useEffect
        } else {
          await load();
        }
      } else if (editing) {
        await axios.patch(`/api/admin/schools/${editing._id}`, payload, { headers: authHeaders() });
        ping('Saved');
        cancel();
        await load();
      }
    } catch (e) {
      setErr(e?.response?.data?.message || 'Save failed.');
    }
  };

  const approve = async (s) => {
    if (s.status === 'active') return;
    try {
      const { data } = await axios.patch(`/api/admin/schools/${s._id}`, { status: 'active' }, { headers: authHeaders() });
      ping('Approved');
      if (data?.adminInitialPassword && data?.adminUser?.email) {
        setCredsToShare({
          label: `${data.name} school admin`,
          email: data.adminUser.email,
          password: data.adminInitialPassword,
        });
      }
      load();
    } catch (e) { setErr(e?.response?.data?.message || 'Approve failed.'); }
  };

  const suspend = async (s) => {
    try {
      await axios.patch(`/api/admin/schools/${s._id}`, { status: 'suspended' }, { headers: authHeaders() });
      ping('Suspended');
      load();
    } catch (e) { setErr(e?.response?.data?.message || 'Suspend failed.'); }
  };

  // Pending school + saved card → tell Stripe to start the subscription.
  // First invoice fires immediately; the webhook flips status to 'active'.
  const startBilling = async (s) => {
    const cycle = s.billingCycle === 'yearly' ? 'yearly' : 'monthly';
    const today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    const msg = [
      `Start billing for ${s.name}?`,
      ``,
      `Their saved card will be charged for the first ${cycle === 'yearly' ? 'year' : 'month'} TODAY (${today}).`,
      `Renewals will fire on this same day every ${cycle === 'yearly' ? 'year' : 'month'} until the school is suspended or deleted.`,
    ].join('\n');
    if (!window.confirm(msg)) return;
    try {
      const { data } = await axios.post(`/api/admin/schools/${s._id}/start-billing`, {}, { headers: authHeaders() });
      ping(`Billing started — ${data.subscriptionStatus}`);
      load();
    } catch (e) { setErr(e?.response?.data?.message || 'Could not start billing.'); }
  };

  const onDelete = async (s) => {
    if (!window.confirm(
      `Permanently delete ${s.name}?\n\n` +
      `This will also remove all teachers, student profiles, classes, ` +
      `progress, favourites, playlists, quiz scores and rewards belonging ` +
      `to this school. This cannot be undone.`
    )) return;
    try {
      const { data } = await axios.delete(`/api/admin/schools/${s._id}`, { headers: authHeaders() });
      const d = data?.deleted || {};
      const summary = `Deleted: ${d.users || 0} user(s), ${d.profiles || 0} profile(s), ${d.classes || 0} class(es)`;
      ping(summary);
      if (data?.hadStripeSubscription) {
        setErr('Heads up: this school had an active Stripe subscription — cancel it in the Stripe dashboard so billing stops.');
      }
      load();
    } catch (e) {
      setErr(e?.response?.data?.message || 'Delete failed.');
    }
  };

  const totals = useMemo(() => ({
    pending:   items.filter((s) => s.status === 'pending').length,
    active:    items.filter((s) => s.status === 'active').length,
    suspended: items.filter((s) => s.status === 'suspended').length,
  }), [items]);

  const showForm = creating || !!editing;

  return (
    <div className="admin-page">
      <header className="admin-page-head" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="admin-page-title">Schools</h1>
          <p className="admin-page-sub">
            {items.length} school{items.length === 1 ? '' : 's'} ·{' '}
            {totals.pending} pending · {totals.active} active · {totals.suspended} suspended.
          </p>
        </div>
        <div style={{ display: 'inline-flex', gap: 8 }}>
          <button type="button" className="admin-btn" onClick={startCreate}>+ Add school</button>
        </div>
      </header>

      <div className="admin-toolbar" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
        {['all', 'pending', 'active', 'suspended'].map((s) => (
          <button
            key={s}
            type="button"
            className={`admin-btn admin-btn-sm ${statusFilter === s ? '' : 'admin-btn-ghost'}`}
            onClick={() => setStatusFilter(s)}
          >
            {s[0].toUpperCase() + s.slice(1)}
          </button>
        ))}
        <input
          className="admin-input"
          style={{ flex: 1, minWidth: 220 }}
          placeholder="Search by name, code, or contact email…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {err && <div className="admin-toast admin-toast-error">{err}</div>}
      {toast && <div className="admin-toast">{toast}</div>}

      {showForm && (
        <div className="admin-card" style={{ marginBottom: 18 }}>
          <h2>{creating ? 'Add a school' : `Edit: ${editing.name}`}</h2>
          {!creating && (
            <p style={{ color: 'var(--admin-fg-muted)', fontSize: '0.84rem', marginTop: 4 }}>
              Code: <strong>{editing.code}</strong> · Created {new Date(editing.createdAt).toLocaleDateString()}
            </p>
          )}
          <div className="admin-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
            <Field label="School name" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} required />
            <Field label="Country" value={form.country} onChange={(v) => setForm((f) => ({ ...f, country: v }))} />
            <Field label="Address" value={form.address} onChange={(v) => setForm((f) => ({ ...f, address: v }))} />
            <Field label="Contact name" value={form.contactName} onChange={(v) => setForm((f) => ({ ...f, contactName: v }))} />
            <Field label="Contact email" type="email" value={form.contactEmail} onChange={(v) => setForm((f) => ({ ...f, contactEmail: v }))} />
            <Field label="Monthly price" value={form.monthlyPrice} onChange={(v) => setForm((f) => ({ ...f, monthlyPrice: v }))} placeholder="e.g. 199.00" />
            <div className="admin-field">
              <label className="admin-label">Currency</label>
              <select className="admin-select" value={form.currency} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}>
                <option value="GBP">GBP (£)</option>
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
              </select>
            </div>
            <div className="admin-field">
              <label className="admin-label">Status</label>
              <select className="admin-select" value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
                <option value="pending">Pending</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
          </div>
          <div className="admin-field" style={{ marginTop: 12 }}>
            <label className="admin-label">Internal notes</label>
            <textarea
              className="admin-input"
              rows={3}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="Anything operators should know about this school (negotiation context, contract reference, etc.)"
            />
          </div>
          <div className="admin-row-actions" style={{ marginTop: 12 }}>
            <button type="button" className="admin-btn" onClick={save}>{creating ? 'Create school' : 'Save changes'}</button>
            <button type="button" className="admin-btn admin-btn-ghost" onClick={cancel}>Cancel</button>
          </div>
        </div>
      )}

      <div className="admin-table-topscroll" ref={topScrollRef}>
        <div ref={topInnerRef} />
      </div>
      <div className="admin-table-wrap" ref={tableWrapRef}>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Code</th>
              <th>Status</th>
              <th>Price</th>
              <th>Teachers</th>
              <th>Students</th>
              <th>Contact</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: 30 }}>Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: 30, opacity: 0.7 }}>No schools yet.</td></tr>
            ) : items.map((s) => {
              const tone = STATUS_TONES[s.status] || { label: s.status, tone: 'muted' };
              return (
                <tr key={s._id}>
                  <td>
                    <strong style={{ color: 'var(--admin-fg-strong)' }}>{s.name}</strong>
                    {s.country ? <div style={{ fontSize: '0.78rem', opacity: 0.65 }}>{s.country}</div> : null}
                  </td>
                  <td><code style={{ fontFamily: 'monospace', letterSpacing: 1 }}>{s.code}</code></td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <span className={`admin-pill admin-pill-${tone.tone}`}>{tone.label}</span>
                      {s.subscriptionStatus && (() => {
                        const subTone = SUB_TONES[s.subscriptionStatus] || { label: s.subscriptionStatus, tone: 'muted' };
                        return (
                          <span className={`admin-pill admin-pill-${subTone.tone}`} style={{ fontSize: '0.7rem' }} title="Stripe subscription status">
                            sub: {subTone.label}
                          </span>
                        );
                      })()}
                    </div>
                  </td>
                  <td>
                    {fmtPrice(s.monthlyPrice, s.currency)}
                    {s.tier && <div style={{ fontSize: '0.72rem', opacity: 0.6, marginTop: 2 }}>{s.tier} · {s.billingCycle || 'monthly'}</div>}
                  </td>
                  <td>{s.teacherCount || 0}</td>
                  <td>{s.studentCount || 0}</td>
                  <td>
                    {s.contactName && <div style={{ fontSize: '0.84rem' }}>{s.contactName}</div>}
                    {s.contactEmail && <div style={{ fontSize: '0.78rem', opacity: 0.7 }}>{s.contactEmail}</div>}
                  </td>
                  <td>
                    <div className="admin-row-actions">
                      {s.status === 'pending' && s.stripePaymentMethodId && !s.stripeSubscriptionId && (
                        <button type="button" className="admin-btn admin-btn-sm" onClick={() => startBilling(s)}>Start billing</button>
                      )}
                      {s.status === 'pending' && !s.stripePaymentMethodId && (
                        <button type="button" className="admin-btn admin-btn-sm" onClick={() => approve(s)}>Approve (no card)</button>
                      )}
                      <button type="button" className="admin-btn admin-btn-ghost admin-btn-sm" onClick={() => startEdit(s)}>Edit</button>
                      {s.status !== 'suspended' && (
                        <button type="button" className="admin-btn admin-btn-ghost admin-btn-sm" onClick={() => suspend(s)}>Suspend</button>
                      )}
                      <button type="button" className="admin-btn admin-btn-danger admin-btn-sm" onClick={() => onDelete(s)}>Delete</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {credsToShare && (
        <div
          className="admin-creds-backdrop"
          onClick={() => setCredsToShare(null)}
        >
          <div className="admin-creds" onClick={(e) => e.stopPropagation()}>
            <div className="admin-creds-eyebrow">{credsToShare.label}</div>
            <h3 className="admin-creds-title">Share these once</h3>
            <p className="admin-creds-body">
              The school admin's password is shown <strong>this once only</strong>. Copy it now and email it to the school — you won't be able to see it again. (You can use "Reset password" later if needed.)
            </p>
            <dl className="admin-creds-fields">
              <div><dt>Email</dt><dd><code>{credsToShare.email}</code></dd></div>
              <div><dt>Password</dt><dd><code>{credsToShare.password}</code></dd></div>
            </dl>
            <button type="button" className="admin-btn" onClick={() => setCredsToShare(null)}>Got it</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', required, placeholder }) {
  return (
    <div className="admin-field">
      <label className="admin-label">{label}{required && <span style={{ color: '#e76f51' }}> *</span>}</label>
      <input
        className="admin-input"
        type={type}
        value={value}
        placeholder={placeholder || ''}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
